import { nextTick } from "vue";
import type { Ref } from "vue";
import { ApolloQueryResult, FetchResult } from "@apollo/client/core";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  CreateDeliveryRequestDocument,
  CreateDeliveryRequestMutation,
  CreateDeliveryRequestMutationVariables,
  CreateMemberDocument,
  CreateMemberMutation,
  CreateMemberMutationVariables,
  UpdateMemberDocument,
  UpdateMemberMutation,
  UpdateMemberMutationVariables,
  UpdateTmpMemberDocument,
  UpdateTmpMemberMutation,
  UpdateTmpMemberMutationVariables,
} from "@/generated/graphql";
import deliveryRegisterInputStore from "@/store/deliveryRegisterInputStore";
import { routerPath } from "@/common/router";
import { useRouter } from "vue-router";

/**
 * 登録実行/会員更新/完了画面遷移に必要な依存をまとめた型。
 */
interface UseDeliveryRequestRegisterSubmitParams {
  overlay: Ref<boolean>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  displayConfirmDialog: Ref<boolean>;
  displayCompletedDialog: Ref<boolean>;
  overDeliveryEndTime: Ref<boolean>;
  deliveryId_0_Flg: Ref<boolean>;
  isDoubleDeli: Ref<boolean>;
  isCharterDeli: Ref<boolean>;
  deliveryStartHourValues: Ref<number | undefined>;
  deliveryStartMinitValues: Ref<{ label: string; deliveryStartMinit: number } | undefined>;
  irregularDeliveryStartTime: Ref<string>;
  irregularDeliveryEndTime: Ref<string>;
  acceptableEndTime: Ref<string>;
  deliveryIdTime: Ref<string>;
  deliveryRequestCreateInput: any;
  memberCreateInput: any;
  memberUpdateInput: any;
  tmpMemberUpdateInput: any;
  operationInput: any;
  dummy_payment_amount: Ref<string>;
  memberNotesBefore: Ref<string>;
  deliveryNotesBefore: Ref<string>;
  messageMemberNotes: Ref<string>;
  messageDeliveryNotes: Ref<string>;
  createDeliveryRequestMutation: Ref<CreateDeliveryRequestMutation | null>;
}

/**
 * 配達依頼の登録実行と、登録前後の画面制御を扱う composable。
 */
export const useDeliveryRequestRegisterSubmit = ({
  overlay,
  errdialog,
  errMessage,
  displayConfirmDialog,
  displayCompletedDialog,
  overDeliveryEndTime,
  deliveryId_0_Flg,
  isDoubleDeli,
  isCharterDeli,
  deliveryStartHourValues,
  deliveryStartMinitValues,
  irregularDeliveryStartTime,
  irregularDeliveryEndTime,
  acceptableEndTime,
  deliveryIdTime,
  deliveryRequestCreateInput,
  memberCreateInput,
  memberUpdateInput,
  tmpMemberUpdateInput,
  operationInput,
  dummy_payment_amount,
  memberNotesBefore,
  deliveryNotesBefore,
  messageMemberNotes,
  messageDeliveryNotes,
  createDeliveryRequestMutation,
}: UseDeliveryRequestRegisterSubmitParams) => {
  const router = useRouter();

  const onClickConfirmCancel = async (): Promise<void> => {
    if (memberNotesBefore.value != "") {
      deliveryRequestCreateInput.member_notes = memberNotesBefore.value;
      memberNotesBefore.value = "";
      messageMemberNotes.value = "";
    }
    if (deliveryNotesBefore.value != "") {
      deliveryRequestCreateInput.delivery_notes = deliveryNotesBefore.value;
      deliveryNotesBefore.value = "";
      messageDeliveryNotes.value = "";
    }
    displayConfirmDialog.value = false;
  };

  const onClickConfirmEntryAndMember = async (): Promise<void> => {
    overlay.value = true;
    try {
      if (!deliveryRegisterInputStore.user_id) {
        await createMember();
      } else {
        await updateMember();
      }

      if (deliveryRegisterInputStore.tmp_member_id) {
        await updateTmpMember();
      }

      const removed = dummy_payment_amount.value.replace(/,/g, "");
      deliveryRequestCreateInput.payment_amount = parseInt(removed, 10);
      if (!(await createDeliveryRequest())) {
        return;
      }

      displayConfirmDialog.value = false;
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
        errMessage.value = "配達依頼の登録に失敗しました。";
      } else {
        console.error("未知のエラー:", err);
        errMessage.value = "予期しないエラーが発生しました。";
      }

      errdialog.value = true;
      return;
    } finally {
      overlay.value = false;
    }

    nextTick(async () => {
      displayCompletedDialog.value = true;
      overlay.value = false;
    });
  };

  const createDeliveryRequest = async (): Promise<boolean> => {
    try {
      const apolloClient = await generateApolloClient();

      const nowDate = new Date();
      const strNowDate =
        String(nowDate.getFullYear()) +
        "-" +
        String(nowDate.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(nowDate.getDate()).padStart(2, "0");
      const acceptableEndTimeAry = acceptableEndTime.value.split(":");

      if (
        deliveryRequestCreateInput.required_delivery_date == strNowDate &&
        (Number(acceptableEndTimeAry[0]) < Number(nowDate.getHours()) ||
          (Number(acceptableEndTimeAry[0]) == Number(nowDate.getHours()) &&
            Number(acceptableEndTimeAry[1]) < Number(nowDate.getMinutes())))
      ) {
        overDeliveryEndTime.value = true;
      }

      if (
        (deliveryRequestCreateInput.member_id == null ||
          deliveryRequestCreateInput.member_id == undefined) &&
        deliveryRegisterInputStore.user_id !== null &&
        deliveryRegisterInputStore.user_id !== undefined
      ) {
        deliveryRequestCreateInput.member_id = Number(
          deliveryRegisterInputStore.user_id
        );
      }
      if (deliveryId_0_Flg.value) {
        // 都度便は「時」「分」をどちらも選択済みの場合のみ登録する。
        if (
          deliveryStartHourValues.value == null ||
          deliveryStartHourValues.value == undefined ||
          deliveryStartMinitValues.value == null ||
          deliveryStartMinitValues.value == undefined
        ) {
          // TODO: 【代替案要検討】都度便の時間未選択エラーは、UI上で選択必須にするため、ここではエラー表示しない。
          // errdialog.value = true;
          // errMessage.value =
          //   "配達希望時間は、時・分ともに入力してください。";
          // return false;
        }
        deliveryRequestCreateInput.irregular_delivery_start_time =
          irregularDeliveryStartTime.value + ":00";
        deliveryRequestCreateInput.irregular_delivery_end_time =
          irregularDeliveryEndTime.value + ":00";
      }
      if (isDoubleDeli.value) {
        deliveryRequestCreateInput.double_deli_oricon_flg = 1;
      } else {
        deliveryRequestCreateInput.double_deli_oricon_flg = 0;
      }
      if (isCharterDeli.value) {
        deliveryRequestCreateInput.charter_deli_oricon_flg = 1;
      } else {
        deliveryRequestCreateInput.charter_deli_oricon_flg = 0;
      }

      const result: FetchResult<CreateDeliveryRequestMutation> =
        await apolloClient.mutate<
          CreateDeliveryRequestMutation,
          CreateDeliveryRequestMutationVariables
        >({
          mutation: CreateDeliveryRequestDocument,
          variables: {
            input: deliveryRequestCreateInput,
            logInput: operationInput,
          },
        });

      createDeliveryRequestMutation.value = result.data!;

      if (
        createDeliveryRequestMutation.value === undefined ||
        createDeliveryRequestMutation.value === null ||
        !createDeliveryRequestMutation.value.createDeliveryRequest?.reception_no
      ) {
        errdialog.value = true;
        errMessage.value = "配達依頼の登録に失敗しました。";
        return false;
      }

      let deliveryStartTimeAny: string[] = [];
      let deliveryEndTimeAny: string[] = [];
      if (deliveryId_0_Flg.value) {
        deliveryStartTimeAny = String(
          createDeliveryRequestMutation.value.createDeliveryRequest
            ?.irregular_delivery_start_time
        ).split(":");
        deliveryEndTimeAny = String(
          createDeliveryRequestMutation.value.createDeliveryRequest
            ?.irregular_delivery_end_time
        ).split(":");
      } else {
        deliveryStartTimeAny = String(
          createDeliveryRequestMutation.value.createDeliveryRequest
            ?.delivery_start_time
        ).split(":");
        deliveryEndTimeAny = String(
          createDeliveryRequestMutation.value.createDeliveryRequest
            ?.delivery_end_time
        ).split(":");
      }

      deliveryIdTime.value =
        createDeliveryRequestMutation.value.createDeliveryRequest
          ?.required_delivery_date +
        " " +
        deliveryStartTimeAny[0].padStart(2, "0") +
        ":" +
        deliveryStartTimeAny[1].padStart(2, "0") +
        " ～ " +
        deliveryEndTimeAny[0].padStart(2, "0") +
        ":" +
        deliveryEndTimeAny[1].padStart(2, "0");

      return true;
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
      } else {
        console.error("未知のエラー:", err);
      }

      errdialog.value = true;
      errMessage.value = "配達依頼の登録に失敗しました。";
      return false;
    }
  };

  const createMember = async (): Promise<void> => {
    memberCreateInput.address1 = deliveryRequestCreateInput.delivery_address1;
    memberCreateInput.address2 = deliveryRequestCreateInput.delivery_address2;
    memberCreateInput.address3 = deliveryRequestCreateInput.delivery_address3;
    memberCreateInput.chain_code = deliveryRequestCreateInput.chain_code;
    memberCreateInput.customer_code =
      deliveryRequestCreateInput.customer_code;
    memberCreateInput.name1 = deliveryRequestCreateInput.delivery_name1;
    memberCreateInput.name2 = deliveryRequestCreateInput.delivery_name2;
    memberCreateInput.name_kana1 =
      deliveryRequestCreateInput.delivery_name_kana1;
    memberCreateInput.name_kana2 =
      deliveryRequestCreateInput.delivery_name_kana2;
    memberCreateInput.notes = deliveryRequestCreateInput.member_notes;
    memberCreateInput.phone = deliveryRequestCreateInput.delivery_phone;
    memberCreateInput.shop_code = deliveryRequestCreateInput.shop_code;
    memberCreateInput.zip_code =
      deliveryRequestCreateInput.delivery_zip_code;
    memberCreateInput.house_card = deliveryRequestCreateInput.house_card;

    const apolloClient = await generateApolloClient();

    const result: FetchResult<CreateMemberMutation> = await apolloClient.mutate<
      CreateMemberMutation,
      CreateMemberMutationVariables
    >({
      mutation: CreateMemberDocument,
      variables: {
        input: memberCreateInput,
        logInput: operationInput,
      },
    });

    const createMemberMutation = result.data!;
    if (createMemberMutation) {
      deliveryRequestCreateInput.member_id = createMemberMutation
        ?.createMember?.member_id
        ? Number(createMemberMutation?.createMember?.member_id)
        : undefined;
    }
  };

  const updateMember = async (): Promise<void> => {
    memberUpdateInput.customer_code = deliveryRequestCreateInput.customer_code;
    memberUpdateInput.chain_code = deliveryRequestCreateInput.chain_code;
    memberUpdateInput.address1 = deliveryRequestCreateInput.delivery_address1;
    memberUpdateInput.address2 = deliveryRequestCreateInput.delivery_address2;
    memberUpdateInput.address3 = deliveryRequestCreateInput.delivery_address3;
    memberUpdateInput.member_id = deliveryRegisterInputStore.user_id;
    memberUpdateInput.name1 = deliveryRequestCreateInput.delivery_name1;
    memberUpdateInput.name2 = deliveryRequestCreateInput.delivery_name2;
    memberUpdateInput.name_kana1 =
      deliveryRequestCreateInput.delivery_name_kana1;
    memberUpdateInput.name_kana2 =
      deliveryRequestCreateInput.delivery_name_kana2;
    memberUpdateInput.search_kana =
      deliveryRequestCreateInput.delivery_name_kana1 +
      deliveryRequestCreateInput.delivery_name_kana2;
    memberUpdateInput.notes = deliveryRequestCreateInput.member_notes;
    memberUpdateInput.phone = deliveryRequestCreateInput.delivery_phone;
    memberUpdateInput.shop_code = deliveryRequestCreateInput.shop_code;
    memberUpdateInput.zip_code =
      deliveryRequestCreateInput.delivery_zip_code;
    memberUpdateInput.house_card = deliveryRequestCreateInput.house_card!;

    const apolloClient = await generateApolloClient();

    const result: FetchResult<UpdateMemberMutation> = await apolloClient.mutate<
      UpdateMemberMutation,
      UpdateMemberMutationVariables
    >({
      mutation: UpdateMemberDocument,
      variables: {
        input: memberUpdateInput,
        logInput: operationInput,
      },
    });

    if (result.data) {
      deliveryRequestCreateInput.member_id = Number(
        deliveryRegisterInputStore.user_id
      );
    }
  };

  const updateTmpMember = async (): Promise<void> => {
    tmpMemberUpdateInput.tmp_member_id = deliveryRegisterInputStore.tmp_member_id;

    const apolloClient = await generateApolloClient();

    await apolloClient.mutate<
      UpdateTmpMemberMutation,
      UpdateTmpMemberMutationVariables
    >({
      mutation: UpdateTmpMemberDocument,
      variables: {
        input: tmpMemberUpdateInput,
        logInput: operationInput,
      },
    });
  };

  const onClickCompletedGoStart = async (): Promise<void> => {
    displayCompletedDialog.value = false;

    nextTick(async () => {
      await router.push({
        path: routerPath.deliveryRequestRegisterStart,
      });
    });
  };

  const onClickGoBack = async (): Promise<void> => {
    await router.push({ path: routerPath.deliveryRequestRegisterStart });
  };

  return {
    onClickConfirmCancel,
    onClickConfirmEntryAndMember,
    createDeliveryRequest,
    createMember,
    updateMember,
    updateTmpMember,
    onClickCompletedGoStart,
    onClickGoBack,
  };
};
